import { AppImages, React, variantProvider } from '@/app'
import { Image } from '@/components'
import { PropsOf, TypeGuards } from '@codeleap/common'

type OrganisationLogoProps = Partial<PropsOf<typeof Image>> & {
  logo?: string
  size?: 'large' | 'medium' | 'small' | 'tiny'
}

export const OrganisationLogo = (props: OrganisationLogoProps) => {
  const { logo, size, ...imageProps } = props

  const _size = size ?? 'medium'

  const hasOrgLogo = TypeGuards.isString(logo)

  const imageSource = logo || AppImages.organisationPlaceholder

  const [naturalSize, setNaturalSize] = React.useState<{ width: number; height: number } | null>(null)

  const imgRef = React.useCallback((node: HTMLImageElement | null) => {
    if (!node) return

    const updateSize = () => {
      if (node.naturalWidth > 0) {
        setNaturalSize({ width: node.naturalWidth, height: node.naturalHeight })
      }
    }

    if (node.complete) {
      updateSize()
    } else {
      node.addEventListener('load', updateSize, { once: true })
    }
  }, [])

  const naturalSizeStyle = naturalSize
    ? {
        maxWidth: naturalSize.width,
        maxHeight: naturalSize.height
      }
    : undefined

  return (
    <img
      ref={imgRef}
      src={imageSource as string}
      style={{
        objectFit: hasOrgLogo ? 'contain' : 'cover',
        ...(imageProps.style as React.CSSProperties),
        ...naturalSizeStyle
      }}
      css={[orgLogoStyles.default, orgLogoStyles[_size], imageProps.style]}
      loading="lazy"
      decoding="async"
      alt={imageProps.alt}
    />
  )
}

export const orgLogoStyles = variantProvider.createComponentStyle(
  theme => ({
    large: {
      height: 112,
      width: 112,
      borderRadius: theme.borderRadius.small,

      [theme.media.down('small')]: {
        borderRadius: theme.borderRadius.medium
      }
    },
    medium: {
      height: 52,
      width: 52,
      borderRadius: theme.borderRadius.tiny
    },
    small: {
      height: 48,
      width: 48,
      borderRadius: theme.borderRadius.tiny
    },
    tiny: {
      height: 32,
      width: 32,
      borderRadius: theme.borderRadius.tiny
    },
    default: {
      // borderRadius: theme.borderRadius.medium,
    },
    placeholder: {
      ...theme.effects.heavy,
      backgroundColor: theme.colors.neutral1
    }
  }),
  true
)
